import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { copy } from 'copy-anything'

import { FilterTicketForm } from 'entities/ticket/models/find-ticket/models'

import { LineBlock } from './ui/line-block'
import { SwitchBlock } from './ui/switch-block'

import s from './styles.module.scss'

interface Switch {
  isWithRepeatRegistration?: boolean
  isWithNightTransition?: boolean
  isWithChangeAirport?: boolean
}

export interface ChangeComport extends Switch {
  range: { min: number; max: number }
}

interface Props {
  filter: FilterTicketForm
  maxValue?: {
    min?: number
    max?: number
  }
  isDisabled?: boolean
  onChangeComfortFilter?: (data: FilterTicketForm) => void
}

export const MIN_MAX_VALUES = {
  min: 0,
  max: 1440,
}

export const ComfortFilter: FC<Props> = ({
  filter,
  maxValue,
  isDisabled,
  onChangeComfortFilter,
}) => {
  const { t } = useTranslation()

  const onSwitchComport = (key: string) => {
    const copyFilter = copy(filter)

    onChangeComfortFilter?.({
      ...copyFilter,
      [key]: !copyFilter[key as keyof object],
    })
  }

  const onRange = (range: number[]) => {
    const copyFilter = copy(filter)

    const [min, max] = range

    onChangeComfortFilter?.({
      ...copyFilter,
      durationOfTransfers: {
        min_duration: min,
        max_duration: max,
      },
    })
  }

  const duration = filter?.durationOfTransfers
  const apiMin = maxValue?.min ?? MIN_MAX_VALUES.min
  const apiMax = maxValue?.max ?? MIN_MAX_VALUES.max
  const currentMaxMinValues = {
    min: Math.min(apiMin, duration?.min_duration ?? apiMin, MIN_MAX_VALUES.min),
    max: Math.max(apiMax, duration?.max_duration ?? 0, MIN_MAX_VALUES.max),
  }

  const isRangeDisabled =
    isDisabled || (maxValue?.min === 0 && maxValue?.max === 0)

  return (
    <div className={s.container}>
      <p className={clsx(s.title, 'bold-17')}>{t('comfort')}</p>

      <LineBlock
        isDisabled={isRangeDisabled}
        onChange={onRange}
        maxValue={currentMaxMinValues}
        value={{
          min: duration?.min_duration ?? apiMin,
          max: duration?.max_duration ?? apiMax,
        }}
      />

      <SwitchBlock
        isChecked={Boolean(filter?.isWithRepeatRegistration)}
        setIsChecked={() => onSwitchComport('isWithRepeatRegistration')}
        title={t('without_reregistration')}
      />

      <SwitchBlock
        isChecked={Boolean(filter?.isWithNightTransition)}
        setIsChecked={() => onSwitchComport('isWithNightTransition')}
        title={t('without_night_transfer')}
      />

      <SwitchBlock
        isChecked={Boolean(filter?.isWithChangeAirport)}
        setIsChecked={() => onSwitchComport('isWithChangeAirport')}
        title={t('without_change_airport')}
      />
    </div>
  )
}
